import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import clsx from 'clsx';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendUserAdequacyMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { useConfirmSuspend, useTunnelChangeHandlers } from 'utils/hooks';
import { fundTypes } from 'utils/constants';
import { boolToStr, formatLabel, isStatusOk, jsonParseSafe, validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { FormBox, TunnelStep } from '../common';
import { CustomTextField, ErrorMessage, RadioLabel, SubmitButton } from 'components';
import { Box, Grid, RadioGroup, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(themeObj => ({
    form: {
        maxWidth: 660,
        width: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    flexContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
    },
    direction: {
        flexDirection: 'column',
    },
    questionLabel: {
        display: 'block',
        fontSize: 16,
        letterSpacing: 0,
        marginBottom: 20,
    },
    error: {
        marginTop: 5,
    },
    input: {
        marginTop: 20,
        width: '100%',
        maxWidth: themeObj.constants.tunnelInputWidth,
        marginLeft: 25,
        [themeObj.breakpoints.down(480)]: {
            marginLeft: 0,
        },
    },
}));

const optionValidationMessages = {
    'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
};

const UserAdequacy = props => {
    const { userStep, handleNextStep } = props;

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { data: messages } = useGetTunnelMessagesQuery();
    const [sendUserAdequacy, { isLoading: buttonLoader, error }] = useSendUserAdequacyMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const classes = useStyles();
    const dispatch = useDispatch();

    const [formStatus, setFormStatus] = useState([
        {
            status: 'started',
            isDirty: false,
            formKey: 0,
            errors: [],
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 1,
            errors: [],
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 2,
            errors: [],
        },
    ]);

    const userAdequacy = useMemo(() => tunnelSubscription?.userAdequacy, [tunnelSubscription]);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const hasOnlyFailedSubmits = useMemo(
        () => userStep?.failedAttemptsNumber > 0 && userStep?.successfulAttemptsNumber === 0,
        [userStep]
    );
    const handleConfirmSuspend = useConfirmSuspend();

    const hasSubmittedOnce = isResubmit || hasOnlyFailedSubmits;

    const [currentForm, setCurrentForm] = useState(0);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    const isFPCI = tunnelSubscription?.investmentFund?.type === fundTypes.FPCI;
    const isFPS = tunnelSubscription?.investmentFund?.type === fundTypes.FPS;
    const isFCPR = tunnelSubscription?.investmentFund?.type === fundTypes.FCPR;
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;

    const defaultForm_0 = useMemo(
        () => ({
            question1: hasSubmittedOnce ? userAdequacy?.question1 : '',
            question2: boolToStr(userAdequacy?.question2, 'Oui', 'Non'),
            question3: hasSubmittedOnce ? userAdequacy?.question3 : '',
            question4: hasSubmittedOnce ? userAdequacy?.question4 : '',
            question5: hasSubmittedOnce ? userAdequacy?.question5 : '',
        }),
        [userAdequacy, hasSubmittedOnce]
    );

    const [form_0, setForm_0] = useState(defaultForm_0);

    const defaultForm_1 = useMemo(
        () => ({
            question6: hasSubmittedOnce ? userAdequacy?.question6 : '',
            question7: hasSubmittedOnce ? userAdequacy?.question7 : '',
            question8: boolToStr(userAdequacy?.question8, 'Oui', 'Non'),
            question9: hasSubmittedOnce ? userAdequacy?.question9 : '',
            question10: hasSubmittedOnce ? userAdequacy?.question10 : '',
        }),
        [userAdequacy, hasSubmittedOnce]
    );

    const [form_1, setForm_1] = useState(defaultForm_1);

    const defaultForm_2 = useMemo(
        () => ({
            question11: hasSubmittedOnce ? userAdequacy?.question11 : '',
            question12: hasSubmittedOnce ? userAdequacy?.question12 : '',
            question13: hasSubmittedOnce ? userAdequacy?.question13 : '',
            question14: hasSubmittedOnce ? userAdequacy?.question14 : '',
            question15: {
                choice: hasSubmittedOnce
                    ? jsonParseSafe(userAdequacy?.question15, {})?.choice?.[0] || ''
                    : '',
                other: hasSubmittedOnce
                    ? jsonParseSafe(userAdequacy?.question15, {})?.other || ''
                    : '',
            },
        }),
        [userAdequacy, hasSubmittedOnce]
    );

    const [form_2, setForm_2] = useState(defaultForm_2);

    const formTouched = useCallback(() => {
        setFormStatus(prevState => [
            ...prevState.slice(0, currentForm),
            { ...prevState[currentForm], isDirty: true },
            ...prevState.slice(currentForm + 1),
        ]);
    }, [currentForm]);

    const handleForms = useCallback(
        (key, setForm) =>
            ({ target: { value, name } }) => {
                if (key === 'question15') {
                    setForm(prevState => ({
                        ...prevState,
                        [key]:
                            !value.includes('option_4') && name === 'choice'
                                ? { ...prevState[key], [name]: value, other: '' }
                                : { ...prevState[key], [name]: value },
                    }));
                } else {
                    setForm(prevState => ({ ...prevState, [key]: value }));
                }
                formTouched();
            },
        [formTouched]
    );

    useEffect(() => {
        if (userAdequacy && hasSubmittedOnce) {
            setFormStatus(prevState =>
                prevState.reduce(
                    (acc, value) => [...acc, { ...value, status: 'completed', isDirty: true }],
                    []
                )
            );
        }
    }, [userAdequacy, hasSubmittedOnce]);

    const schema = useMemo(() => {
        if (tunnelSubscription.indirectUntransferred) return {};
        return {
            0: {
                question1: Joi.string().required().messages(optionValidationMessages),
                question2: Joi.boolean().required().truthy('Oui').falsy('Non').messages({
                    'boolean.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                }),
                question3: Joi.string().required().messages(optionValidationMessages),
                question4: Joi.string().required().messages(optionValidationMessages),
                question5: Joi.string().required().messages(optionValidationMessages),
            },
            1: {
                question6: Joi.string().required().messages(optionValidationMessages),
                question7: Joi.string().required().messages(optionValidationMessages),
                question8: Joi.boolean().required().truthy('Oui').falsy('Non').messages({
                    'boolean.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                }),
                question9: Joi.string().required().messages(optionValidationMessages),
                question10: Joi.string().required().messages(optionValidationMessages),
            },
            2: {
                question11: Joi.string().required().messages(optionValidationMessages),
                question12: Joi.string().required().messages(optionValidationMessages),
                question13: Joi.string().required().messages(optionValidationMessages),
                question14: Joi.string().required().messages(optionValidationMessages),
                question15: Joi.object().keys({
                    choice: Joi.string().required().messages({
                        'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                        'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                    }),
                    other: Joi.when('choice', {
                        is: 'step37.question_15.option_4',
                        then: Joi.string(),
                        otherwise: Joi.any(),
                    }),
                }),
            },
        };
    }, [tunnelSubscription]);

    const currentFormIsValid = useCallback(
        (formSchema, form, formKey, displayErrors = true) => {
            if (tunnelSubscription.indirectUntransferred) return true;
            const index = formStatus.findIndex(x => x.formKey === formKey);
            const { valid, newErrors } = validateSchema(formSchema, form);
            let errors = { ...newErrors };

            if (displayErrors) {
                setFormStatus(prevState => [
                    ...prevState.slice(0, index),
                    {
                        ...prevState[index],
                        errors: valid ? {} : errors,
                    },
                    ...prevState.slice(index + 1),
                ]);
            }
            return valid;
        },
        [formStatus, tunnelSubscription]
    );

    const formArray = useMemo(() => [form_0, form_1, form_2], [form_0, form_1, form_2]);
    const defaultFormArray = useMemo(
        () => [defaultForm_0, defaultForm_1, defaultForm_2],
        [defaultForm_0, defaultForm_1, defaultForm_2]
    );

    useEffect(() => {
        let valid = true;
        for (let index = 0; index < formStatus.length; index++) {
            const form = formArray[index];
            if (!currentFormIsValid(schema[index], form, index, false)) {
                valid = false;
                setIsSubmitDisabled(true);
                break;
            }
        }
        if (valid) {
            setIsSubmitDisabled(false);
        }
    }, [formStatus, currentFormIsValid, formArray, schema]);

    const handleStepFormBox = useCallback(
        key => () => {
            const formIndex = formStatus.findIndex(x => x.formKey === key);
            let unTouchedFormIndex = formStatus.findIndex(form => form.status === 'notStarted');
            unTouchedFormIndex = unTouchedFormIndex !== -1 ? unTouchedFormIndex : formIndex + 1;

            if (currentFormIsValid(schema[key], formArray[formIndex], key)) {
                setCurrentForm(unTouchedFormIndex);
                setFormStatus(prevState => {
                    if (unTouchedFormIndex !== -1) {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            ...prevState.slice(formIndex + 1, unTouchedFormIndex),
                            {
                                ...prevState[unTouchedFormIndex],
                                status: 'started',
                                isDirty: false,
                            },
                            ...prevState.slice(unTouchedFormIndex + 1),
                        ];
                    } else {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            {
                                ...prevState[formIndex + 1],
                                status: 'started',
                                isDirty: true,
                            },
                            ...prevState.slice(formIndex + 2),
                        ];
                    }
                });
            }
        },
        [currentFormIsValid, formArray, formStatus, schema]
    );

    const handleStepEditFormBox = useCallback(
        key => () => {
            const formIndex = formStatus.findIndex(x => x.formKey === key); // ID of the form that is clicked
            const currentFormKey = formStatus[currentForm].formKey;
            const allFormsCompleted = !formStatus.find(x => x.status !== 'completed');
            if (allFormsCompleted) {
                setFormStatus(prevState => {
                    return [
                        ...prevState.slice(0, formIndex),
                        {
                            ...prevState[formIndex],
                            status: 'started',
                            isDirty: true,
                        },
                        ...prevState.slice(formIndex + 1),
                    ];
                });
                setCurrentForm(formIndex);
                return;
            }

            if (
                currentFormIsValid(schema[currentFormKey], formArray[currentForm], currentFormKey)
            ) {
                setFormStatus(prevState => {
                    if (formIndex > currentForm) {
                        return [
                            ...prevState.slice(0, currentForm),
                            {
                                ...prevState[currentForm],
                                status: 'completed',
                                isDirty: true,
                            },
                            ...prevState.slice(currentForm + 1, formIndex),
                            { ...prevState[formIndex], status: 'started', isDirty: true },
                            ...prevState.slice(formIndex + 1),
                        ];
                    } else {
                        return [
                            ...prevState.slice(0, formIndex),
                            {
                                ...prevState[formIndex],
                                status: 'started',
                                isDirty: true,
                            },
                            ...prevState.slice(formIndex + 1, currentForm),
                            { ...prevState[currentForm], status: 'completed', isDirty: true },
                            ...prevState.slice(currentForm + 1),
                        ];
                    }
                });
                setCurrentForm(formIndex);
            }
        },
        [currentForm, currentFormIsValid, formArray, formStatus, schema]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            sendUserAdequacy({
                payload: { ...form_0, ...form_1, ...form_2 },
                callback: () => {
                    if (afterSubmitCallback) {
                        afterSubmitCallback();
                    } else {
                        handleNextStep(true);
                    }
                },
            });
        },
        [sendUserAdequacy, handleNextStep, form_0, form_1, form_2]
    );

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !isEqualWith(defaultFormArray, formArray, (objValue, othValue) => {
            return objValue.every((formObject, i) => isEqual(formObject, othValue[i]));
        });
    }, [formArray, defaultFormArray]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    useEffect(() => {
        if (error?.status === 422) {
            dispatch(
                ineligibleSet({
                    open: true,
                    abandon: false,
                    canConfirm: true,
                    message: texts.NOT_ADEQUATE_P1(''),
                    subMessage: texts.NOT_ADEQUATE_P2,
                    confirmMessage: texts.PAUSE_AND_WAIT,
                    onConfirm: handleConfirmSuspend,
                })
            );
        }
    }, [dispatch, error?.status, handleConfirmSuspend]);

    const subtitle = useMemo(() => {
        if (isFPCI || isFPS) {
            return (
                <React.Fragment>
                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                        {isFPS
                            ? texts.COMPTE_TENU_FPS(
                                  tunnelSubscription?.investmentFund?.promoter?.name
                              )
                            : texts.COMPTE_TENU(tunnelSubscription?.investmentFund?.promoter?.name)}
                    </Typography>
                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                        {texts.OBJECTIV(tunnelSubscription?.investmentFund?.name)}
                    </Typography>
                    <Typography variant="subtitle1">{texts.OBJECTIV_2}</Typography>
                </React.Fragment>
            );
        } else if (isFCPR) {
            return <Typography variant="subtitle1">{texts.USER_ADEQUACY_SUBTITLE_FCPR}</Typography>;
        } else if (isSLP) {
            return (
                <React.Fragment>
                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                        {texts.COMPTE_TENU_SLP(tunnelSubscription?.investmentFund?.promoter?.name)}
                    </Typography>
                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                        {texts.OBJECTIV_SLP(tunnelSubscription?.investmentFund?.name)}
                    </Typography>
                    <Typography variant="subtitle1">{texts.OBJECTIV_2}</Typography>
                </React.Fragment>
            );
        }
    }, [isFPCI, isFPS, isFCPR, isSLP, tunnelSubscription]);

    return (
        <TunnelStep title={texts.YOUR_INVESTOR_PROFILE} subtitle={subtitle}>
            <Grid container direction="column" alignItems="center" justifyContent="center">
                <form className={classes.form} onSubmit={e => e.preventDefault()}>
                    <FormBox
                        handleEditFormBox={handleStepEditFormBox(0)}
                        handleFormBox={handleStepFormBox(0)}
                        status={formStatus[0].status}
                        label={texts.SENSIBILITE_AUX_RISQUES}
                        completedLabel={texts.SENSIBILITE_AUX_RISQUES}
                        showInfoButton={false}
                        largeLabel
                    >
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_1.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question1', setForm_0)}
                                value={form_0.question1}
                            >
                                {[1, 2, 3, 4].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_1.option_${i}`]}
                                        value={`step37.question_1.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[0].errors?.question1 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[0].errors?.question1}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {messages['step37.question_2.label']}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question2', setForm_0)}
                                value={form_0.question2}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={messages['general_options.yes']}
                                    checked={form_0.question2 === 'Oui'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={messages['general_options.no']}
                                    checked={form_0.question2 === 'Non'}
                                />
                            </RadioGroup>
                            {!!formStatus[0].errors?.question2 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[0].errors?.question2}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_3.label'])}
                            </label>
                            <RadioGroup
                                className={clsx(classes.flexContainer, classes.direction)}
                                onChange={handleForms('question3', setForm_0)}
                                value={form_0.question3}
                            >
                                {[1, 2, 3].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_3.option_${i}`]}
                                        value={`step37.question_3.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[0].errors?.question3 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[0].errors?.question3}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_4.label'])}
                            </label>
                            <RadioGroup
                                className={clsx(classes.flexContainer, classes.direction)}
                                onChange={handleForms('question4', setForm_0)}
                                value={form_0.question4}
                            >
                                {[1, 2, 3].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_4.option_${i}`]}
                                        value={`step37.question_4.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[0].errors?.question4 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[0].errors?.question4}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_5.label'])}
                            </label>
                            <RadioGroup
                                className={clsx(classes.flexContainer, classes.direction)}
                                onChange={handleForms('question5', setForm_0)}
                                value={form_0.question5}
                            >
                                {[1, 2, 3, 4].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_5.option_${i}`]}
                                        value={`step37.question_5.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[0].errors?.question5 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[0].errors?.question5}
                                />
                            )}
                        </Box>
                    </FormBox>
                    <FormBox
                        handleEditFormBox={handleStepEditFormBox(1)}
                        handleFormBox={handleStepFormBox(1)}
                        status={formStatus[1].status}
                        showInfoButton={false}
                        label={texts.COMPREHENSION_DU_PRODUIT}
                        completedLabel={texts.COMPREHENSION_DU_PRODUIT}
                        largeLabel
                    >
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_6.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question6', setForm_1)}
                                value={form_1.question6}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={'general_options.yes'}
                                    checked={form_1.question6 === 'general_options.yes'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={'general_options.no'}
                                    checked={form_1.question6 === 'general_options.no'}
                                />
                                <RadioLabel
                                    label={messages['general_options.unknown']}
                                    value={'general_options.unknown'}
                                    checked={form_1.question6 === 'general_options.unknown'}
                                />
                            </RadioGroup>
                            {!!formStatus[1].errors?.question6 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[1].errors?.question6}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_7.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question7', setForm_1)}
                                value={form_1.question7}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={'general_options.yes'}
                                    checked={form_1.question7 === 'general_options.yes'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={'general_options.no'}
                                    checked={form_1.question7 === 'general_options.no'}
                                />
                                <RadioLabel
                                    label={messages['general_options.unknown']}
                                    value={'general_options.unknown'}
                                    checked={form_1.question7 === 'general_options.unknown'}
                                />
                            </RadioGroup>
                            {!!formStatus[1].errors?.question7 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[1].errors?.question7}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {messages['step37.question_8.label']}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question8', setForm_1)}
                                value={form_1.question8}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={messages['general_options.yes']}
                                    checked={form_1.question8 === 'Oui'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={messages['general_options.no']}
                                    checked={form_1.question8 === 'Non'}
                                />
                            </RadioGroup>
                            {!!formStatus[1].errors?.question8 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[1].errors?.question8}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(
                                    messages['step37.question_9.label'].replace(
                                        '%sgp_name%',
                                        tunnelSubscription?.investmentFund?.sgp?.name
                                    )
                                )}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question9', setForm_1)}
                                value={form_1.question9}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={'general_options.yes'}
                                    checked={form_1.question9 === 'general_options.yes'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={'general_options.no'}
                                    checked={form_1.question9 === 'general_options.no'}
                                />
                                <RadioLabel
                                    label={messages['general_options.unknown']}
                                    value={'general_options.unknown'}
                                    checked={form_1.question9 === 'general_options.unknown'}
                                />
                            </RadioGroup>
                            {!!formStatus[1].errors?.question9 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[1].errors?.question9}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_10.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question10', setForm_1)}
                                value={form_1.question10}
                            >
                                <RadioLabel
                                    label={messages['general_options.yes']}
                                    value={'general_options.yes'}
                                    checked={form_1.question10 === 'general_options.yes'}
                                />
                                <RadioLabel
                                    label={messages['general_options.no']}
                                    value={'general_options.no'}
                                    checked={form_1.question10 === 'general_options.no'}
                                />
                                <RadioLabel
                                    label={messages['general_options.unknown']}
                                    value={'general_options.unknown'}
                                    checked={form_1.question10 === 'general_options.unknown'}
                                />
                            </RadioGroup>
                            {!!formStatus[1].errors?.question10 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[1].errors?.question10}
                                />
                            )}
                        </Box>
                    </FormBox>
                    <FormBox
                        isLast
                        handleEditFormBox={handleStepEditFormBox(2)}
                        handleFormBox={handleStepFormBox(2)}
                        status={formStatus[2].status}
                        label={texts.VOS_ATTENTES}
                        completedLabel={texts.VOS_ATTENTES}
                        showInfoButton={false}
                        largeLabel
                    >
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_11.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question11', setForm_2)}
                                value={form_2.question11}
                            >
                                {[1, 2].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_11.option_${i}`]}
                                        value={`step37.question_11.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[2].errors?.question1 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[2].errors?.question1}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_12.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question12', setForm_2)}
                                value={form_2.question12}
                            >
                                {[1, 2, 3].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_12.option_${i}`]}
                                        value={`step37.question_12.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[2].errors?.question12 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[2].errors?.question12}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_13.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question13', setForm_2)}
                                value={form_2.question13}
                            >
                                {[1, 2, 3, 4].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_13.option_${i}`]}
                                        value={`step37.question_13.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[2].errors?.question13 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[2].errors?.question13}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_14.label'])}
                            </label>
                            <RadioGroup
                                className={classes.flexContainer}
                                onChange={handleForms('question14', setForm_2)}
                                value={form_2.question14}
                            >
                                {[1, 2, 3, 4].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_14.option_${i}`]}
                                        value={`step37.question_14.option_${i}`}
                                    />
                                ))}
                            </RadioGroup>
                            {!!formStatus[2].errors?.question14 && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[2].errors?.question14}
                                />
                            )}
                        </Box>
                        <Box mb="20px">
                            <label className={classes.questionLabel}>
                                {formatLabel(messages['step37.question_15.label'])}
                            </label>
                            <RadioGroup
                                name="choice"
                                className={clsx(classes.flexContainer, classes.direction)}
                                onChange={handleForms('question15', setForm_2)}
                                value={form_2.question15.choice}
                            >
                                {[1, 2, 3, 4].map(i => (
                                    <RadioLabel
                                        key={i}
                                        label={messages[`step37.question_15.option_${i}`]}
                                        value={`step37.question_15.option_${i}`}
                                    />
                                ))}
                                {form_2.question15?.choice === 'step37.question_15.option_4' && (
                                    <CustomTextField
                                        className={classes.input}
                                        name="other"
                                        value={form_2.question15?.other}
                                        onChange={handleForms('question15', setForm_2)}
                                        error={formStatus[2].errors['form_2']?.input}
                                        showError={!!formStatus[2].errors['form_2']?.input}
                                    />
                                )}
                            </RadioGroup>
                            {!!formStatus[2].errors?.['question15,choice'] && (
                                <ErrorMessage
                                    className={classes.error}
                                    message={formStatus[2].errors?.['question15,choice']}
                                />
                            )}
                        </Box>
                    </FormBox>
                    <SubmitButton
                        text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                        disabled={isSubmitDisabled || buttonLoader}
                        loading={buttonLoader}
                        onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                    />
                </form>
            </Grid>
        </TunnelStep>
    );
};

export default UserAdequacy;
